import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { equipmentAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdmin, canModifyContent } from '../utils/roles';

const EQUIPMENT_TYPES = {
  BOAT: 'boat',
  ERGOMETER: 'ergometer',
  OAR: 'oar',
  ACCESSORY: 'accessory',
};

const EQUIPMENT_TYPE_LABELS = {
  [EQUIPMENT_TYPES.BOAT]: 'Boat',
  [EQUIPMENT_TYPES.ERGOMETER]: 'Ergometer',
  [EQUIPMENT_TYPES.OAR]: 'Oar',
  [EQUIPMENT_TYPES.ACCESSORY]: 'Accessory',
};

const STATUS_COLORS = {
  available: 'bg-green-100 text-green-800 border-green-200',
  in_use: 'bg-blue-100 text-blue-800 border-blue-200',
  maintenance: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  broken: 'bg-red-100 text-red-800 border-red-200',
};

const Equipment = () => {
  const { user } = useContext(AuthContext);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    equipment_type: EQUIPMENT_TYPES.BOAT,
    description: '',
    status: 'available',
    capacity: '',
    location: '',
  });
  const [filters, setFilters] = useState({
    equipment_type: '',
    status: '',
    search: '',
  });

  useEffect(() => {
    loadEquipment();
  }, []);

  const loadEquipment = async () => {
    try {
      setLoading(true);
      const data = await equipmentAPI.getEquipment();
      setEquipment(data);
    } catch (error) {
      console.error('Failed to load equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const equipmentData = {
        ...formData,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
      };

      if (editingEquipment) {
        await equipmentAPI.updateEquipment(editingEquipment.id, equipmentData);
      } else {
        await equipmentAPI.createEquipment(equipmentData);
      }

      setShowCreateModal(false);
      setEditingEquipment(null);
      resetForm();
      loadEquipment();
    } catch (error) {
      console.error('Failed to save equipment:', error);
      alert('Failed to save equipment. Please try again.');
    }
  };

  const handleDelete = async (equipmentId) => {
    if (!confirm('Are you sure you want to delete this equipment?')) {
      return;
    }

    try {
      await equipmentAPI.deleteEquipment(equipmentId);
      loadEquipment();
    } catch (error) {
      console.error('Failed to delete equipment:', error);
      alert('Failed to delete equipment. Please try again.');
    }
  };

  const handleEdit = (item) => {
    setEditingEquipment(item);
    setFormData({
      name: item.name,
      equipment_type: item.equipment_type,
      description: item.description || '',
      status: item.status,
      capacity: item.capacity?.toString() || '',
      location: item.location || '',
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      equipment_type: EQUIPMENT_TYPES.BOAT,
      description: '',
      status: 'available',
      capacity: '',
      location: '',
    });
  };

  const getStatusLabel = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
  };

  const getStatusClass = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800 border-gray-200';
    return STATUS_COLORS[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const filteredEquipment = equipment.filter((item) => {
    if (filters.equipment_type && item.equipment_type !== filters.equipment_type) return false;
    if (filters.status && item.status !== filters.status) return false;
    if (filters.search && !item.name.toLowerCase().includes(filters.search.toLowerCase()))
      return false;
    return true;
  });

  const getEquipmentStats = () => {
    const stats = {
      total: equipment.length,
      available: equipment.filter((e) => e.status === 'available').length,
      in_use: equipment.filter((e) => e.status === 'in_use').length,
      maintenance: equipment.filter((e) => e.status === 'maintenance').length,
      broken: equipment.filter((e) => e.status === 'broken').length,
    };
    return stats;
  };

  const stats = getEquipmentStats();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Equipment Management</h1>
          <p className="text-gray-600">Manage boats, ergs, and other rowing equipment.</p>
        </div>

        {isAdmin(user) && (
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            Add Equipment
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
        <div className="card text-center">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600">Total Equipment</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-green-600">{stats.available}</div>
          <div className="text-sm text-gray-600">Available</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-blue-600">{stats.in_use}</div>
          <div className="text-sm text-gray-600">In Use</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-yellow-600">{stats.maintenance}</div>
          <div className="text-sm text-gray-600">Maintenance</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-red-600">{stats.broken}</div>
          <div className="text-sm text-gray-600">Broken</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <h3 className="mb-4 text-lg font-medium text-gray-900">Filters</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Equipment Type</label>
            <select
              value={filters.equipment_type}
              onChange={(e) => setFilters({ ...filters, equipment_type: e.target.value })}
              className="form-input"
            >
              <option value="">All Types</option>
              {Object.entries(EQUIPMENT_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="form-input"
            >
              <option value="">All Statuses</option>
              <option value="available">Available</option>
              <option value="in_use">In Use</option>
              <option value="maintenance">Maintenance</option>
              <option value="broken">Broken</option>
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Search</label>
            <input
              type="text"
              placeholder="Search equipment..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="form-input"
            />
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={() => setFilters({ equipment_type: '', status: '', search: '' })}
            className="btn-outline"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Equipment List */}
      <div className="card">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Equipment ({filteredEquipment.length})
          </h3>
        </div>

        {filteredEquipment.length === 0 ? (
          <div className="py-12 text-center">
            <h4 className="mb-2 text-lg font-medium text-gray-900">No equipment found</h4>
            <p className="mb-4 text-gray-500">
              {equipment.length === 0
                ? 'No equipment has been added yet.'
                : 'No equipment matches your current filters.'}
            </p>
            {canModifyContent(user) && equipment.length === 0 && (
              <button onClick={() => setShowCreateModal(true)} className="btn-primary">
                Add First Equipment
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredEquipment.map((item) => (
              <div key={item.id} className="rounded-lg border border-gray-200 p-4 hover:bg-gray-50">
                <div className="mb-3 flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">{item.name}</h4>
                    <p className="text-sm text-gray-600">
                      {EQUIPMENT_TYPE_LABELS[item.equipment_type]}
                    </p>
                  </div>
                  <span
                    className={`rounded border px-2 py-1 text-xs font-medium ${getStatusClass(item.status)}`}
                  >
                    {getStatusLabel(item.status)}
                  </span>
                </div>

                {item.description && (
                  <p className="mb-3 text-sm text-gray-600">{item.description}</p>
                )}

                <div className="space-y-1 text-sm text-gray-600">
                  {item.capacity && (
                    <div>
                      <strong>Capacity:</strong> {item.capacity} people
                    </div>
                  )}
                  {item.location && (
                    <div>
                      <strong>Location:</strong> {item.location}
                    </div>
                  )}
                </div>

                {canModifyContent(user) && (
                  <div className="mt-4 flex gap-2 border-t pt-3">
                    <button
                      onClick={() => handleEdit(item)}
                      className="btn-secondary flex-1 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="btn-outline flex-1 text-sm text-red-600 hover:border-red-200 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white">
            <div className="border-b p-6">
              <h3 className="text-lg font-medium text-gray-900">
                {editingEquipment ? 'Edit Equipment' : 'Add Equipment'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 p-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input"
                  placeholder="e.g., Single Scull #1"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Equipment Type *
                </label>
                <select
                  required
                  value={formData.equipment_type}
                  onChange={(e) => setFormData({ ...formData, equipment_type: e.target.value })}
                  className="form-input"
                >
                  {Object.entries(EQUIPMENT_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Status *</label>
                <select
                  required
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="form-input"
                >
                  <option value="available">Available</option>
                  <option value="in_use">In Use</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="broken">Broken</option>
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Capacity</label>
                <input
                  type="number"
                  min="1"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  className="form-input"
                  placeholder="Number of people (for boats)"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="form-input"
                  placeholder="e.g., Dock A, Erg Room"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-input"
                  placeholder="Optional description or notes..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingEquipment(null);
                    resetForm();
                  }}
                  className="btn-outline"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingEquipment ? 'Update Equipment' : 'Add Equipment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Equipment;
